import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import type { UserCourseLesson } from "api/schemas/UserCourse";
import { useGetLessons } from "api/services/content/hooks";
import { useProfileContext } from "contexts/ProfileContext";
import type { LocationState } from "routes/library/views/Confirmation";
import { LibraryTablePagination } from "routes/patients/PatientProfile/components/UserCourse/components/LibraryTablePagination";
import { useLibraryListSettings } from "routes/patients/PatientProfile/components/UserCourse/utils/useLibraryListSettings";
import { LibraryItemSkeleton } from "shared/atoms/SkeletonLayout";
import { sortAlphabetically } from "utils/array/sort";
import { useTargetJointOptions } from "utils/form";
import type { LibraryListSettingsStorageKey } from "utils/storage/storageConfig";

import { LessonLibraryItem } from "./LessonLibraryItem";
import type { TypeOption } from "./LibraryHeader";
import { LibraryHeader } from "./LibraryHeader";

interface LessonLibraryListProps {
  initialSelectedIds: number[];
  setError?: (msg: string | null) => void;
  multiSelect?: boolean;
  onSelectedLessonsChange?: (lessons: UserCourseLesson[]) => void;
  onClick?: (id: number) => void;
  libraryListSettingsStorageKey?: LibraryListSettingsStorageKey;
  persistLibraryListSettings?: boolean;
}

const existsInSelectedLessons = (selectedLessons: UserCourseLesson[], item: UserCourseLesson) =>
  !!selectedLessons.find(e => e.id === item.id);

const filterLessons = (selectedTypeOption: TypeOption, initialSelectedIds: number[]) => (item: UserCourseLesson) => {
  const added = initialSelectedIds.includes(item.id);
  if ((selectedTypeOption === "added" && !added) || (selectedTypeOption === "new" && added)) {
    return false;
  }
  return true;
};

export const LessonLibraryList = ({
  initialSelectedIds,
  setError,
  multiSelect,
  onSelectedLessonsChange,
  onClick,
  libraryListSettingsStorageKey,
  persistLibraryListSettings,
}: LessonLibraryListProps) => {
  const {
    setSearchTerm,
    debouncedSearchTerm,
    previewJoint,
    setPreviewJoint,
    selectedSort,
    setSelectedSort,
    selectedTypeOption,
    setSelectedTypeOption,
    limit,
    currentPage,
    setCurrentPage,
  } = useLibraryListSettings(libraryListSettingsStorageKey, persistLibraryListSettings);
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const { state } = useLocation() as { state: LocationState };
  const targetJointOptions = useTargetJointOptions();

  const [filteredLessons, setFilteredLessons] = useState<UserCourseLesson[]>([]);
  const [selectedLessons, setSelectedLessons] = useState<UserCourseLesson[]>([]);
  const [locale] = useState(profile?.locale || "en-US");

  useEffect(() => {
    if (onSelectedLessonsChange) {
      onSelectedLessonsChange(selectedLessons);
    }
  }, [selectedLessons]);

  const {
    refetch: refetchLessons,
    data,
    error: isError,
    isLoading,
  } = useGetLessons({
    joint: previewJoint !== "all" ? previewJoint : undefined,
    limit,
    locale,
    page: currentPage + 1,
    search: debouncedSearchTerm || undefined,
  });

  const allLessons = data?.items || [];
  const totalCount = data?.metadata?.total_count || 0;

  useEffect(() => {
    refetchLessons();
  }, []);

  useEffect(() => {
    if (setError) {
      if (isError) {
        setError(t("errors.generic"));
      } else {
        setError(null);
      }
    }
  }, [isError, setError, t]);

  // set initial selection
  useEffect(() => {
    if (!selectedLessons.length && allLessons && initialSelectedIds.length) {
      const lessons = [...allLessons].filter(item => initialSelectedIds.includes(item.id));
      if (lessons.length) {
        setSelectedLessons(lessons);
      }
    }
  }, [allLessons]);

  // generate filtered lessons
  useEffect(() => {
    if (!allLessons?.length) {
      setFilteredLessons([]);
    }
    if (allLessons?.length) {
      const fe = [...allLessons].filter(filterLessons(selectedTypeOption, initialSelectedIds));
      const sortedLessons = sortAlphabetically({ list: fe, selectedSort, keySelector: lesson => lesson.title });

      if (state?.item && "id" in state.item && "title" in state.item) {
        // This is a hack to make sure that the newly created lesson always is at the top of the list and has the NEW badge.
        // In order to not risk duplicated lessons (the temporarily added one from location state and the one from the API)
        // we filter out the one from the API if it exists in the list
        const lessonItem = state.item as unknown as UserCourseLesson;
        const filtered = sortedLessons.filter(ex => ex.id !== lessonItem.id);
        setFilteredLessons([lessonItem, ...filtered]);
      } else {
        setFilteredLessons(sortedLessons);
      }
    }
  }, [allLessons, selectedSort, debouncedSearchTerm, selectedTypeOption, initialSelectedIds, selectedLessons]);

  const markAll = () => {
    if (allLessons) {
      setSelectedLessons(prevState => {
        const newLessons = filteredLessons.filter(
          filteredLesson => !selectedLessons.find(selectedLesson => selectedLesson.id === filteredLesson.id)
        );
        return [...prevState, ...newLessons];
      });
    }
  };

  const unmarkAll = () => {
    if (allLessons) {
      setSelectedLessons(prevState => {
        const newState = prevState.filter(se => !filteredLessons.find(ve => ve.id === se.id));
        return [...newState];
      });
    }
  };

  return (
    <Library>
      <LibraryHeader
        initialSelectedIds={initialSelectedIds}
        multiSelect={multiSelect}
        numberOfSelectedItems={selectedLessons.length}
        markAll={markAll}
        unmarkAll={unmarkAll}
        jointOptions={[
          { label: t("patients.pain_location.all"), value: "all" },
          ...sortAlphabetically({ list: targetJointOptions }),
        ]}
        onSortChange={sort => {
          setSelectedSort(sort);
        }}
        onPreviewJointChange={joint => {
          setPreviewJoint(joint);
        }}
        onSearchChange={_search => {
          setSearchTerm(_search);
        }}
        onSelectedTypeOptionChange={type => {
          setSelectedTypeOption(type);
        }}
        libraryListSettingsStorageKey={libraryListSettingsStorageKey}
        pagination={
          <LibraryTablePagination
            count={totalCount}
            rowsPerPage={limit}
            page={!totalCount ? 0 : currentPage}
            onPageChange={(event, newPage) => {
              setCurrentPage(newPage);
            }}
            rowsPerPageOptions={[]}
          />
        }
      />

      <SearchResults>
        {/* eslint-disable-next-line react/no-array-index-key */}
        {isLoading && [...Array(12)].map((_, index) => <LibraryItemSkeleton key={`lesson_skeleton_${index}`} />)}

        {!isLoading && allLessons?.length === 0 && <NoResultsBox>{t("usercourse.no_result")}</NoResultsBox>}

        {filteredLessons.map(item => {
          const checked = existsInSelectedLessons(selectedLessons, item);
          return (
            <LessonLibraryItem
              key={`lessonpickeritem_${item.id}`}
              item={item}
              showAddButton={multiSelect}
              checked={checked}
              onClick={() => {
                if (onClick) {
                  onClick(item.id);
                }

                if (multiSelect) {
                  if (checked) {
                    setSelectedLessons(prevState => prevState.filter(i => i.id !== item.id));
                  } else {
                    setSelectedLessons(prevState => [...prevState, item]);
                  }
                } else {
                  setSelectedLessons([item]);
                }
              }}
            />
          );
        })}
      </SearchResults>
    </Library>
  );
};

const Library = styled.div`
  padding-bottom: 16px;
`;
const SearchResults = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const NoResultsBox = styled.div`
  width: 100%;
  text-align: center;
  font-size: 16px;
  padding: 20px;
  background-color: #f8f8f8;
`;
